import styles from 'css/src/components/common/ui/Select/CustomOption.module.scss';

import React from 'react';
import type { OptionProps } from 'react-select';
import { components } from 'react-select';
import classNames from 'classnames';

import type { SelectOption } from 'js/src/components/common/ui/Select/types';

/**
 * This CustomOption extends the default react-select option component with the following features:
 * - supports the data.locked property to display a lock icon
 * - supports the data.disabled property to disable the option (with an optional data.disabledLabel)
 * - supports the data.action property to display an icon that triggers a callback
 */
const CustomOption = <Option extends SelectOption<unknown>>(
    props: OptionProps<Option, boolean>,
) => {
    const { children, className, data, isDisabled, isFocused, isSelected } = props;

    let lockedNode = null;
    if (data.locked) {
        lockedNode = (
            <i translate="no" className="material-symbols-outlined workspace-premium">
                workspace_premium
            </i>
        );
    }

    let actionNode = null;
    if (data.action) {
        actionNode = (
            <i
                translate="no"
                onClick={data.action.callback}
                className={classNames(
                    'material-symbols-outlined ms-l5-auto',
                    styles['action-icon'],
                    data.action.className,
                )}
            >
                {data.action.icon}
            </i>
        );
    }

    let disabledLabelNode = null;
    if (data.disabledLabel && data.disabled) {
        disabledLabelNode = (
            <span className={`${styles['disabled-label']} w-50`}>{data.disabledLabel}</span>
        );
    }

    // override the className
    const newClassName = classNames(
        {
            option: true,
            'option--is-disabled': isDisabled || data.disabled,
            'option--is-focused': isFocused,
            'option--is-selected': isSelected,
        },
        className,
        data.className,
        'd-flex',
        'align-items-center',
        styles['custom-option'],
    );

    return (
        <components.Option className={newClassName} {...props}>
            <span className={`${styles['option-children']} flex-grow-1`}>{children}</span>
            {disabledLabelNode}
            {lockedNode}
            {actionNode}
        </components.Option>
    );
};

export default CustomOption;
